/**
 * The search filters, as a drawer from the left (like the category drawer),
 * opened by the search bar's Filter button.
 *
 * Every page gets the standard filters: price, condition, deals only,
 * quality only, featured (utils/deal-quality-filter.ts has the cutoffs). Where the listings carry them, the attribute filters follow --
 * gender, age, size, color, brand, material -- with their counts for this
 * category or group under the choices already made (GET /api/search/facets),
 * refreshed as the shopper picks. A section with no options is left out, so a
 * category that has none shows the standard set alone.
 *
 * Apply on a browse page stays on that page with the filters in its URL; on
 * any other page it searches (/search), with the category picked here. Either
 * way the set goes into the recent searches, so it can be clicked back to.
 */
import { parseTagFacets, toggleTagFacet } from '../utils/tag-facet-param';
import React, { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { useUI, useRouterContext, useProduct } from '../contexts';
import { useSearch } from '../contexts/SearchProvider';
import { CONDITIONS, Cats, getCategoryById, getCategoryGroupBySlug, getSubcategoryBySlug } from '../generated/static-categories';
import { useSearchFacets } from '../hooks/useSearchFacets';
import { recordFacetSelection } from '../utils/track-facets';
import { newFacetPicks } from '../utils/facet-affinity';
import {
  filtersFromQuery, filtersToQuery, withoutFilters, type AttributeFilterName, type FilterState,
} from '../utils/search-filter-state';
import type { FacetOption, SearchFacets } from '../schemas/api/search/facets';

/** Set on <body> while the drawer is open. styles/pages/product.scss stands the flip buttons down under it. */
export const FILTER_DRAWER_OPEN_CLASS = 'filter-panel-open';

/** Options a long section shows before "Show all". */
export const COLLAPSED_OPTIONS = 12;

/** Attribute sections in the order they appear, each with its heading. */
export const FACET_SECTIONS: ReadonlyArray<{ key: AttributeFilterName; title: string }> = [
  { key: 'gender', title: 'Gender' },
  { key: 'ageGroup', title: 'Age' },
  { key: 'size', title: 'Size' },
  { key: 'color', title: 'Color' },
  { key: 'brand', title: 'Brand' },
  { key: 'material', title: 'Material' },
];

const first = (v: string | string[] | undefined) => (Array.isArray(v) ? v[0] : v);

/**
 * What a browse route is scoped to, read from the route itself: a subcategory
 * page (or a subcategory slug on the category route) is a categoryId, a group
 * page a categoryGroupId. Null off the browse pages.
 */
export function browseScope(pathname: string, query: Record<string, string | string[] | undefined>): { categoryId?: number; categoryGroupId?: number } | null {
  if (!pathname.startsWith('/browse/')) return null;
  const sub = first(query.subcategory);
  const cat = first(query.category);
  const subcategory = sub ? getSubcategoryBySlug(sub) : null;
  if (subcategory) return { categoryId: subcategory.id };
  const group = cat ? getCategoryGroupBySlug(cat) : null;
  if (group) return { categoryGroupId: group.id };
  const asSubcategory = cat ? getSubcategoryBySlug(cat) : null;
  return asSubcategory ? { categoryId: asSubcategory.id } : {};
}

/** A price box's value as a whole dollar amount, or undefined when empty or not a positive number. */
export function parsePrice(input: string): number | undefined {
  const n = Math.floor(Number(input));
  return input.trim() !== '' && Number.isFinite(n) && n > 0 ? n : undefined;
}

/** A list with `value` added or taken out. Undefined when it ends up empty. */
export function toggleValue(list: string[] | undefined, value: string): string[] | undefined {
  const current = list ?? [];
  const next = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
  return next.length ? next : undefined;
}

/** A section's options: chosen ones always shown (even at no count), then the rest by count. */
export function sectionOptions(options: readonly FacetOption[], chosen: readonly string[] | undefined): FacetOption[] {
  const picked = new Set(chosen ?? []);
  const known = new Set(options.map(o => o.value));
  const missing = [...picked].filter(v => !known.has(v)).map(v => ({ value: v, label: v, count: 0, icon: null }));
  return [...options, ...missing];
}

function Chip({ active, onClick, children, testId }: { active: boolean; onClick: () => void; children: React.ReactNode; testId?: string }) {
  return (
    <button type="button" className={`filter-panel-btn filter-drawer-chip ${active ? 'active' : ''}`} aria-pressed={active} onClick={onClick} data-testid={testId}>
      {children}
    </button>
  );
}

function FacetSection({ title, name, options, chosen, onToggle }: {
  title: string;
  /** The attribute key, `type`, or a tag section's key (utils/category-facet-layout.ts). */
  name: string;
  options: FacetOption[];
  chosen: string[] | undefined;
  onToggle: (value: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [needle, setNeedle] = useState('');
  const all = sectionOptions(options, chosen);
  // Long lists (brands, breeds, teams, models) get a search box once opened.
  const searchable = all.length > COLLAPSED_OPTIONS;
  const matching = needle ? all.filter(o => o.label.toLowerCase().includes(needle.toLowerCase())) : all;
  const shown = expanded || needle ? matching : matching.slice(0, COLLAPSED_OPTIONS);
  return (
    <section className="filter-drawer-section" data-testid={`filter-section-${name}`}>
      <h3 className="filter-panel-heading filter-drawer-heading">{title}</h3>
      {searchable && expanded && (
        <input
          className="filter-drawer-search filter-panel-input-group"
          type="search"
          placeholder={`Find a ${title.toLowerCase()}`}
          value={needle}
          onChange={e => setNeedle(e.target.value)}
          data-testid={`filter-search-${name}`}
        />
      )}
      <div className="filter-drawer-chips">
        {shown.map(option => (
          <Chip key={option.value} active={Boolean(chosen?.includes(option.value))} onClick={() => onToggle(option.value)} testId={`filter-${name}-${option.value}`}>
            {name === 'color' && <span className={`filter-swatch filter-swatch--${option.value}`} aria-hidden="true" />}
            {option.icon && <img src={option.icon} alt="" className="filter-drawer-icon" />}
            <span>{option.label}</span>
            <span className="filter-drawer-count">{option.count.toLocaleString()}</span>
          </Chip>
        ))}
      </div>
      {matching.length > COLLAPSED_OPTIONS && !needle && (
        <button type="button" className="filter-drawer-more" onClick={() => setExpanded(e => !e)} data-testid={`filter-more-${name}`}>
          {expanded ? 'Show fewer' : `Show all ${matching.length}`}
        </button>
      )}
    </section>
  );
}

/**
 * The page's sections in its category group's order (utils/category-facet-layout.ts),
 * each with what is chosen in it and how to toggle. Off a category the API
 * sends no sections, and the attribute sections stand alone as before.
 */
export function drawerSections(facets: SearchFacets | null, draft: FilterState): Array<{
  key: string; title: string; options: FacetOption[]; chosen: string[] | undefined; attribute: boolean;
}> {
  const picked = parseTagFacets(draft.tf) ?? {};
  if (facets?.sections) {
    return facets.sections
      .map(s => {
        const attribute = s.kind === 'attribute';
        const chosen = attribute ? draft[s.key as AttributeFilterName] : picked[s.key];
        return { key: s.key, title: s.title, options: s.options, chosen, attribute };
      })
      .filter(s => s.options.length > 0 || (s.chosen?.length ?? 0) > 0);
  }
  return facetSectionsToShow(facets, draft).map(s => ({ key: s.key, title: s.title, options: facets?.[s.key] ?? [], chosen: draft[s.key], attribute: true }));
}

/** Attribute sections that have something to offer, in display order. */
export function facetSectionsToShow(facets: SearchFacets | null, draft: FilterState): typeof FACET_SECTIONS[number][] {
  return FACET_SECTIONS.filter(s => (facets?.[s.key]?.length ?? 0) > 0 || (draft[s.key]?.length ?? 0) > 0);
}

/**
 * The drawer's heading names what it filters: "Pets Filters", "Dresses
 * Filters". The owner, 2026-10-10: "each filter set needs to adjust the sidebar
 * title. instead of filters it should say \"[Category] Filters\"". A category
 * page names its category, a group page or a product its group, the search page
 * the group picked in the drawer; with none picked, plain "Filters".
 */
export function drawerTitle(scope: { categoryId?: number; categoryGroupId?: number } | null | undefined): string {
  const category = scope?.categoryId ? getCategoryById(scope.categoryId)?.label : undefined;
  const group = scope?.categoryGroupId ? Cats[scope.categoryGroupId]?.label : undefined;
  const name = category ?? group;
  return name ? `${name} Filters` : 'Filters';
}

/**
 * On a product page, the product's category group. The owner, 2026-10-10:
 * "when i'm on a product in a category, i'd like ... the sidepanel for
 * filtering to be for that category. so i'm on pets, i want pets, not fashion
 * brands". Off a product page, or before the product has loaded, null.
 */
export function productScope(pathname: string, product: { categoryGroupId?: string | number | null } | null | undefined): { categoryGroupId: number } | null {
  if (!pathname.startsWith('/product')) return null;
  const group = Number(product?.categoryGroupId);
  return Number.isInteger(group) && group > 0 && Cats[group] ? { categoryGroupId: group } : null;
}

/**
 * Category groups whose drawer has no Show row (Deals Only, Quality Only,
 * Featured Only). The owner, 2026-10-12: on Pets those "don't match adoptions,
 * which ends up dominating the category". An adoption has no market price to
 * be a deal against and no brand or materials to score.
 */
export const GROUPS_WITHOUT_SHOW_TOGGLES: ReadonlySet<number> = new Set([16]);

/** Whether the drawer offers the Show row for this scope: a subcategory answers for its group. */
export function showsShowToggles(scope: { categoryId?: number; categoryGroupId?: number } | null | undefined): boolean {
  const group = scope?.categoryId ? getCategoryById(scope.categoryId)?.group : scope?.categoryGroupId;
  return !(typeof group === 'number' && GROUPS_WITHOUT_SHOW_TOGGLES.has(group));
}

/** The filters with the Show row's three taken off, for a scope that has no Show row. */
export function withoutShowToggles(state: FilterState): FilterState {
  const { deals: _deals, quality: _quality, featured: _featured, ...rest } = state;
  return rest;
}

const FilterDrawer: React.FC = () => {
  const { filterDrawerOpen, setFilterDrawerOpen } = useUI();
  const { searchParams, setSearchParams, setRecentSearch, setCurrentCategoryGroupId } = useSearch();
  const { push } = useRouterContext();
  const router = useRouter();
  const { productDetail } = useProduct();
  const scopeFromProduct = productScope(router.pathname, productDetail);
  const scopeFromRoute: { categoryId?: number; categoryGroupId?: number } | null = browseScope(router.pathname, router.query) ?? scopeFromProduct;
  const onBrowse = scopeFromRoute !== null;
  // A shop page is a search for its matched tags (components/shop-page.tsx):
  // filters stay on it in the URL, as on browse, instead of leaving for /search
  // and dropping the tags. Unlike browse it has no category scope, so the
  // category picker stays and its pick goes in the URL too.
  const onShop = router.pathname.startsWith('/shop/');

  // On a product page the last search's filters belong to wherever it was (a
  // brand from Fashion on a dog bed): they carry over only within the group.
  const applied = useMemo(() => {
    const fromSearch = filtersFromQuery(searchParams as Record<string, never>);
    if (!scopeFromProduct || Number(searchParams.categoryGroupId) === scopeFromProduct.categoryGroupId) return fromSearch;
    return {};
  }, [searchParams, scopeFromProduct?.categoryGroupId]);
  const [draft, setDraft] = useState<FilterState>(applied);
  const [minInput, setMinInput] = useState('');
  const [maxInput, setMaxInput] = useState('');

  // Each opening starts from what is applied now.
  useEffect(() => {
    if (!filterDrawerOpen) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- re-seeds the editable draft from the applied filters each time the drawer opens; the shopper edits it in between
    setDraft(applied);
    setMinInput(applied.minPrice ? String(applied.minPrice) : '');
    setMaxInput(applied.maxPrice ? String(applied.maxPrice) : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterDrawerOpen]);

  // Nothing behind an open drawer should still take taps; Escape closes it.
  useEffect(() => {
    if (!filterDrawerOpen) return undefined;
    document.body.classList.add(FILTER_DRAWER_OPEN_CLASS);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFilterDrawerOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.classList.remove(FILTER_DRAWER_OPEN_CLASS);
      document.removeEventListener('keydown', onKey);
    };
  }, [filterDrawerOpen, setFilterDrawerOpen]);

  const withPrices: FilterState = { ...draft, minPrice: parsePrice(minInput), maxPrice: parsePrice(maxInput) };
  const scope = scopeFromRoute ?? { categoryGroupId: draft.categoryGroupId };
  const { facets, loading } = useSearchFacets(scope, withPrices, filterDrawerOpen);
  const sections = drawerSections(facets, draft);
  const title = drawerTitle(scope);
  const showToggles = showsShowToggles(scope);
  const hasQuery = Boolean(searchParams.q && searchParams.q.trim());

  const close = () => setFilterDrawerOpen(false);

  const apply = (picked: FilterState) => {
    // A Show filter left on from elsewhere (a link, the last search) would hide every adoption.
    const next = showToggles ? picked : withoutShowToggles(picked);
    const q = (searchParams.q ?? '').trim();
    if (onBrowse || onShop) {
      // Stay on this category: its route keeps the scope, the URL carries the filters.
      const kept = withoutFilters({ ...router.query } as Record<string, unknown>);
      delete kept.category;
      delete kept.subcategory;
      delete kept.keywords;
      // On shop the drawer owns the category too, so a cleared pick must leave the URL.
      if (onShop) delete kept.categoryGroupId;
      const query = new URLSearchParams();
      for (const [k, v] of Object.entries(kept)) if (typeof v === 'string' && v) query.set(k, v);
      // A product page browses its category group with the filters.
      const path = scopeFromProduct ? `/browse/${Cats[scopeFromProduct.categoryGroupId].slug}` : router.asPath.split('?')[0];
      // The product route's own params (id, slug) mean nothing on a browse page.
      if (scopeFromProduct) for (const k of [...query.keys()]) if (k !== 'sortBy') query.delete(k);
      for (const [k, v] of Object.entries(filtersToQuery(next, { withCategoryGroup: onShop }))) query.set(k, v);
      const qs = query.toString();
      push(qs ? `${path}?${qs}` : path);
    } else {
      const group = next.categoryGroupId ?? 0;
      setCurrentCategoryGroupId(group);
      // undefined deletes a key in setSearchParams, so filters taken off are cleared.
      const cleared: Record<string, unknown> = {};
      for (const key of ['minPrice', 'maxPrice', 'itemCondition', 'sold', 'shippable', 'featured', 'gender', 'ageGroup', 'size', 'color', 'brand', 'material', 'tf']) cleared[key] = undefined;
      setSearchParams({ ...cleared, ...next, q, categoryGroupId: group, page: 1 });
      const query = new URLSearchParams({ ...(q ? { q } : {}), ...filtersToQuery(next, { withCategoryGroup: true }) });
      const qs = query.toString();
      push(qs ? `/search?${qs}` : '/');
    }
    setRecentSearch({ q, ...next, categoryGroupId: scopeFromProduct ? scopeFromProduct.categoryGroupId : onBrowse ? searchParams.categoryGroupId : next.categoryGroupId });
    recordFacetSelection(newFacetPicks(next, applied), {
      query: q || undefined,
      categoryId: scopeFromRoute?.categoryId,
      categoryGroupId: scopeFromRoute ? scopeFromRoute.categoryGroupId : next.categoryGroupId || undefined,
    });
    close();
  };

  // On a product page Clear empties the choices and stays: there is no list here to unfilter.
  const clear = () => {
    if (scopeFromProduct) {
      setDraft({});
      setMinInput('');
      setMaxInput('');
      return;
    }
    apply(onBrowse ? {} : { categoryGroupId: 0 });
  };

  const toggle = (key: AttributeFilterName, value: string) => setDraft(d => ({ ...d, [key]: toggleValue(d[key], value) }));
  const toggleTag = (key: string, value: string) => setDraft(d => ({ ...d, tf: toggleTagFacet(d.tf, key, value) }));
  const toggleCondition = (id: number) => setDraft(d => {
    const now = d.itemCondition ?? [];
    const next = now.includes(id) ? now.filter(c => c !== id) : [...now, id];
    return { ...d, itemCondition: next.length ? next : null };
  });

  const applyLabel = !hasQuery && facets && !loading ? `Show ${facets.total.toLocaleString()} result${facets.total === 1 ? '' : 's'}` : 'Apply Filters';

  return (
    <>
      <div className="filter-drawer-bg" onClick={close} data-testid="filter-drawer-bg" />
      <aside className="filter-drawer filter-panel" aria-label={title} aria-hidden={!filterDrawerOpen} data-testid="filter-drawer">
        <header className="filter-drawer-header">
          <h2 className="filter-drawer-title" data-testid="filter-drawer-title">{title}</h2>
          <button type="button" className="filter-drawer-close" onClick={close} aria-label="Close filters" data-testid="filter-drawer-close">&times;</button>
        </header>

        <div className="filter-drawer-body">
          {!onBrowse && (
            <section className="filter-drawer-section">
              <h3 className="filter-panel-heading filter-drawer-heading">Category</h3>
              <select
                className="filter-panel-select form-select"
                value={draft.categoryGroupId || 0}
                onChange={e => setDraft(d => ({ ...d, categoryGroupId: Number(e.target.value) || undefined }))}
                data-testid="filter-category"
              >
                {Object.keys(Cats)
                  // generated/static-categories gives every group a numeric sortOrder.
                  .sort((a, b) => Cats[a].sortOrder - Cats[b].sortOrder)
                  .map(id => <option key={id} value={id}>{Cats[id].label}</option>)}
              </select>
            </section>
          )}

          <section className="filter-drawer-section">
            <h3 className="filter-panel-heading filter-drawer-heading">Price</h3>
            <div className="filter-drawer-price">
              <label className="filter-panel-input-group filter-drawer-price-box">
                <span className="filter-panel-dollar">$</span>
                <input className="filter-panel-input" type="number" inputMode="numeric" min="0" placeholder="Min" aria-label="Minimum price"
                  value={minInput} onChange={e => setMinInput(e.target.value)} data-testid="filter-min-price" />
              </label>
              <span className="filter-panel-separator">to</span>
              <label className="filter-panel-input-group filter-drawer-price-box">
                <span className="filter-panel-dollar">$</span>
                <input className="filter-panel-input" type="number" inputMode="numeric" min="0" placeholder="Max" aria-label="Maximum price"
                  value={maxInput} onChange={e => setMaxInput(e.target.value)} data-testid="filter-max-price" />
              </label>
            </div>
          </section>

          <section className="filter-drawer-section">
            <h3 className="filter-panel-heading filter-drawer-heading">Condition</h3>
            <div className="filter-drawer-chips">
              <Chip active={!draft.itemCondition?.length} onClick={() => setDraft(d => ({ ...d, itemCondition: null }))} testId="filter-condition-any">Any</Chip>
              {CONDITIONS.map(c => (
                <Chip key={c.id} active={Boolean(draft.itemCondition?.includes(c.id))} onClick={() => toggleCondition(c.id)} testId={`filter-condition-${c.id}`}>{c.label}</Chip>
              ))}
            </div>
          </section>

          {sections.map(s => (
            <FacetSection
              key={s.key}
              title={s.title}
              name={s.key}
              options={s.options}
              chosen={s.chosen}
              onToggle={v => (s.attribute ? toggle(s.key as AttributeFilterName, v) : toggleTag(s.key, v))}
            />
          ))}

          {showToggles && (
          <section className="filter-drawer-section" data-testid="filter-section-show">
            <h3 className="filter-panel-heading filter-drawer-heading">Show</h3>
            <div className="filter-drawer-chips">
              <Chip active={Boolean(draft.deals)} onClick={() => setDraft(d => ({ ...d, deals: !d.deals || undefined }))} testId="filter-deals">Deals Only</Chip>
              <Chip active={Boolean(draft.quality)} onClick={() => setDraft(d => ({ ...d, quality: !d.quality || undefined }))} testId="filter-quality">Quality Only</Chip>
              <Chip active={Boolean(draft.featured)} onClick={() => setDraft(d => ({ ...d, featured: !d.featured || undefined }))} testId="filter-featured">Featured Only</Chip>
            </div>
          </section>
          )}
        </div>

        <footer className="filter-panel-footer filter-drawer-footer">
          <button type="button" className="filter-panel-clear-btn filter-drawer-clear" onClick={clear} data-testid="filter-clear">Clear</button>
          <button type="button" className="filter-panel-apply-btn filter-drawer-apply" onClick={() => apply(withPrices)} data-testid="filter-apply">{applyLabel}</button>
        </footer>
      </aside>
    </>
  );
};

export default FilterDrawer;
